import { useState, useEffect } from 'react';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { useForm, useWatch, get } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';

import { Strings } from '@app/constants';
import {
  FormProvider,
  Iconify,
  RHFSelect,
  RHFTextField,
  TimeSlotPicker,
  formatTimeSlots,
} from '@app/components';
import { Header } from '@app/sections/header';
import { useDialog, useRequestState } from '@app/hooks';
import { TIME_SCHEDULE } from '@app/configs';
import { IInterviewee } from '../types';
import { addIntervieweeSchema } from '../validators';
import { getCandidates } from '@app/modules/candidates/slice';
import { addInterviewee, getCandidateSearch, updateInterviewee } from '../slice';
import {
  selectAddIntervieweeState,
  selectCandidateIntervieweeState,
  selectUpdateIntervieweeState,
} from '../selecter';
import { selectEmployeesState, getEmployees } from '@app/modules/employees';
import { isWeekend } from '@app/utils';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IInterviewee;
}

let interview: string;

const parseTimeSlots = (timeSlots: string | string[] | undefined): string[] => {
  if (!timeSlots) return [];
  if (Array.isArray(timeSlots)) return timeSlots;
  if (typeof timeSlots === 'string') {
    return [timeSlots];
  }
  return [];
};

const AddInterviewee = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();
  const timeSlotDialog = useDialog();

  const [query, setQuery] = useState();

  const initialPreferredTimeSlots = row?.interviewTime ? parseTimeSlots(row.interviewTime) : [];

  const methods = useForm<IInterviewee>({
    resolver: yupResolver(addIntervieweeSchema) as any,
    defaultValues: {
      interviewTime: get(row, 'interviewTime', ''),
      interviewer: get(row, 'interviewer'),
      candidateEmail: get(row, 'candidateEmail', ''),
      candidateName: get(row, 'candidateName', ''),
      preferredTimeSlots: initialPreferredTimeSlots,
    },
  });

  const handleSelect = (data: string[]) => {
    interview = data[0];

    methods.setValue('preferredTimeSlots', data);
    if (data.length) {
      methods.clearErrors('preferredTimeSlots');
      methods.setValue('interviewTime', formatTimeSlots(data));
    }
  };
  const preferredTimeSlots =
    useWatch({ control: methods.control, name: 'preferredTimeSlots' }) ?? [];

  useEffect(() => {
    if (row?.interviewTime && !preferredTimeSlots.length) {
      const parsedSlots = parseTimeSlots(row.interviewTime);
      methods.setValue('preferredTimeSlots', parsedSlots);
    }
  }, [row?.interviewTime, methods, preferredTimeSlots.length]);

  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateIntervieweeState : selectAddIntervieweeState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(getCandidates());
    dispatch(getCandidateSearch());
  }, [dispatch]);

  const onSubmit = (form: IInterviewee) => {
    row
      ? dispatch(updateInterviewee({ ...form, id: row.id, interviewTime: interview }))
      : dispatch(addInterviewee({ ...form, interviewTime: interview }));
  };

  const { data } = useRequestState({
    stateSelector: selectCandidateIntervieweeState,
  });

  const handleCandidateSelect = (candidateName: string) => {
    const selectedCandidate = data?.find(
      (candidate: any) =>
        candidate.firstName === candidateName || candidate.lastName === candidateName
    );
    if (selectedCandidate) {
      methods.setValue('candidateEmail', selectedCandidate.email || '');
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      const fullName = `${selectedCandidate.firstName} ${selectedCandidate.lastName}`;
      methods.setValue('candidateName', fullName);
    }
  };
  const managerState = useSelector(selectEmployeesState);

  useEffect(() => {
    if (!query) {
      dispatch(getCandidateSearch());
    } else {
      dispatch(getCandidateSearch(query));
    }
  }, [dispatch, query]);

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={row ? Strings.addCandidate.update : Strings.button.addNew}
            onClose={onClose}
          />
          <Divider />
          <DialogContent>
            <Stack spacing={2} my={2}>
              <Autocomplete
                disabled={!!row}
                options={
                  data
                    ?.filter((option: any) => option.status !== Strings.status.rejected)
                    .map((option: any) => option.firstName || option.lastName) ?? []
                }
                filterOptions={(options: string[], state) =>
                  options.filter((option) =>
                    option.toLowerCase().includes(state.inputValue.toLowerCase())
                  )
                }
                defaultValue={row?.candidateName}
                renderInput={(params: any) => (
                  <RHFTextField
                    name="candidateName"
                    {...params}
                    label={Strings.addCandidate.candidateName}
                    required
                    onChange={(event: any) => {
                      setQuery(event.target.value);
                    }}
                    onBlur={(event) => {
                      if (!event.target.value) {
                        methods.setValue('candidateEmail', 'candidateName');
                      }
                    }}
                  />
                )}
                onChange={(event, value) => {
                  if (typeof value === 'string') {
                    handleCandidateSelect(value);
                  }
                }}
              />
              <RHFTextField disabled name="candidateEmail" label={Strings.field.email} />

              <RHFSelect
                required
                name="interviewer"
                type="select"
                label={Strings.addCandidate.interviewer}
              >
                {(managerState.data?.records ?? [])
                  .filter((option) => option.roleId === 2 || option.roleId === 1)
                  .map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.firstName}
                    </MenuItem>
                  ))}
              </RHFSelect>
              <RHFTextField
                name="interviewTime"
                required
                value={formatTimeSlots(preferredTimeSlots)}
                label={Strings.addCandidate.interviewTime}
                fullWidth
                onClick={timeSlotDialog.show}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ pr: 0 }}>
                      <IconButton>
                        <Iconify icon="eva:calendar-fill" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {timeSlotDialog.visible && (
                <TimeSlotPicker
                  open
                  onClose={timeSlotDialog.hide}
                  onSelect={handleSelect}
                  startsHours={TIME_SCHEDULE.START_OF_THE_DAY}
                  initialValue={preferredTimeSlots}
                  endsTo={TIME_SCHEDULE.END_OF_THE_DAY}
                  slotDuration={TIME_SCHEDULE.SLOT_TIME_LENGTH}
                  isMultipleSelectable={false}
                  shouldDisableDate={isWeekend}
                />
              )}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={loading}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};
export { AddInterviewee };
